import { useEffect, useRef } from 'react'
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query'
import { useFetchAllRows, UseFetchAllRowsResult } from '@/hooks/useFetchAllRows'
import { getShipperListV2 } from '@/services/shipper/apis'
import { API_COMMON_CACHE_TIME } from '@/utils/consts'
import {
  OUTBOUND_PROGRESS_MAX_ATTEMPTS,
  OUTBOUND_PROGRESS_MAX_FAILURES,
  OUTBOUND_PROGRESS_POLLING_INTERVAL_MS,
  GENERAL_OUTBOUND_FETCH_SIZE,
  OUTBOUND_PROGRESS_TERMINAL_STATUSES,
  SCREEN_MENU_CODE,
  SEARCH_FAILED_MESSAGE,
  SHIPPER_LIST_MAX_SIZE,
} from '@/services/outbound/generalOutbound/consts'
import {
  confirmGeneralOutbounds,
  getOutboundProgress,
  saveGeneralOutbounds,
  searchGeneralOutbounds,
} from '@/services/outbound/generalOutbound/apis'
import {
  ConfirmGeneralOutboundPayload,
  ConfirmGeneralOutboundRes,
  GeneralOutboundItem,
  OutboundProgress,
  SaveGeneralOutboundPayload,
  SaveGeneralOutboundRes,
  SearchGeneralOutboundParams,
} from '@/services/outbound/generalOutbound/types'

export const generalOutboundQueryKeys = {
  /**
   * 맨 앞이 화면 메뉴 코드다. 탭을 닫을 때 `MenuOnSessionWrap.remove` 가 queryKey 에 메뉴 코드가 든
   * 캐시만 지운다 — 빼면 탭을 닫아도 조회 결과가 남는다.
   */
  all: [SCREEN_MENU_CODE, 'GENERAL_OUTBOUND'] as const,
  /**
   * 조회 키는 조건만으로 만든다.
   *
   * 예전에는 조회 순번을 키에 섞었다. 같은 조건으로 [조회]를 다시 눌러도 서버에 묻게 하려던 것인데,
   * 순번이 마운트 지역 값이라 다른 메뉴를 다녀오면 0 부터 다시 세어 키가 어긋났다. 그러면 캐시가
   * 있어도 못 쓰고 전량 재조회한다 — 사람이 말한 "완전히 새로고침" 이다(결정 12).
   *
   * 같은 조건 재조회는 호출부가 `refetch()` 로 낸다. 조건이 바뀐 경우는 키가 달라져 자동으로 돌므로
   * 호출부가 둘을 갈라 부른다 — 안 가르면 요청이 두 번 나간다.
   */
  list: (params: GeneralOutboundListParams) => [...generalOutboundQueryKeys.all, 'list', params] as const,
  shippers: [SCREEN_MENU_CODE, 'GENERAL_OUTBOUND', 'shippers'] as const,
  progress: (progressKey: string) => [...generalOutboundQueryKeys.all, 'progress', progressKey] as const,
}

/** 구간 경계에서 겹친 행을 거르는 기본키 — 모듈 상수라 참조가 바뀌지 않는다. */
const rowIdOf = (row: GeneralOutboundItem): number => row.ID

/** 조회 조건 — BE 구간(page·pageSize)은 훅이 채운다. 키에 넣지 않아 페이지를 넘겨도 다시 받지 않는다. */
export type GeneralOutboundListParams = Omit<SearchGeneralOutboundParams, 'page' | 'pageSize'>

/**
 * 일반출고 목록 — 전량 조회(LOGISPRD-15268). BE 한 번 상한(10,000)씩 끊어 받는다.
 *
 * 진입 즉시 조회하지 않는다 — 화주코드가 필수라 조회 버튼으로만 실행한다(`enabled`).
 * BE 는 실패도 HTTP 200 + `rtnChk:false` 로 내려주는 구간이 있어(조회 예외 처리) 여기서 예외로 승격한다.
 * 문구를 `response.data.message` 에 싣는다 — 실패 알럿은 공용 훅 meta(errorToast)로 전역이 띄우고, 전역은 그 자리에서 서버 문구를 읽는다.
 */
export const useGetGeneralOutboundList = (
  params: GeneralOutboundListParams,
  /** 호출부가 반드시 정한다 — 기본값을 두면 진입 즉시 조회가 조용히 살아난다. */
  enabled: boolean,
): UseFetchAllRowsResult<GeneralOutboundItem> =>
  useFetchAllRows({
    queryKey: generalOutboundQueryKeys.list(params),
    // BE page 는 1부터 센다(1.0 GI31 과 같음). 훅은 0부터 넘긴다.
    fetchPage: async (page, size) => {
      const res = await searchGeneralOutbounds({ ...params, page: page + 1, pageSize: size ?? GENERAL_OUTBOUND_FETCH_SIZE })
      if (!res.rtnChk) {
        const message = res.message || SEARCH_FAILED_MESSAGE
        throw Object.assign(new Error(message), { response: { data: { message } } })
      }
      const items = res.rtnData ?? []
      return { items, totalCount: res.params?.totalCnt ?? items.length }
    },
    chunkSize: GENERAL_OUTBOUND_FETCH_SIZE,
    getKey: rowIdOf,
    enabled,
  })

/** 화주명 필터 옵션. 클라이언트 검색 전제로 전건을 한 번에 받는다(ST11 선례). */
export const useGetShipperOptions = (): UseQueryResult<{ value: string; label: string }[], Error> =>
  useQuery({
    queryKey: generalOutboundQueryKeys.shippers,
    queryFn: async () => {
      const res = await getShipperListV2({ shipperNames: [], logisticsServiceTypes: [], page: 0, size: SHIPPER_LIST_MAX_SIZE })
      return (res?.data?.items ?? []).map((s) => ({ value: s.shipperCode, label: s.shipperName || s.shipperCode }))
    },
    staleTime: API_COMMON_CACHE_TIME,
    // 실패 안내는 화면이 신규 디자인 알럿(PboAlert)으로 띄운다 — 전역 알럿(옛 mwms-ds 모달)은 끈다.
    meta: { noErrorMessage: true },
  })

export const useSaveGeneralOutbounds = (): UseMutationResult<SaveGeneralOutboundRes, Error, SaveGeneralOutboundPayload> =>
  useMutation({ mutationFn: saveGeneralOutbounds })

export const useConfirmGeneralOutbounds = (): UseMutationResult<
  ConfirmGeneralOutboundRes,
  Error,
  ConfirmGeneralOutboundPayload
> => useMutation({ mutationFn: confirmGeneralOutbounds })

/**
 * 저장 진행률 폴링을 언제 멈출지 정한다. 훅 밖으로 뺀 이유는 실제 간격을 기다리지 않고 테스트하기 위해서다.
 *
 * 멈추는 경우가 넷이다. 앞 셋은 1.0 `progressPollingEpic` 과 같은 기준이다.
 * 1. 끝난 상태(COMPLETED·FAILED). FAILED 를 빼면 실패했을 때 폴링이 안 멈춰 저장 버튼이 잠긴 채로 남는다.
 * 2. 진행률이 100 에 닿음. 상태가 아직 RUNNING 이어도 1.0 은 여기서 끝냈다(`progress >= 100`).
 * 3. 연속 실패가 상한에 닿음. 응답이 안 오면 status 가 undefined 라 1·2 로는 절대 멈추지 않는다.
 * 4. 시도 횟수가 상한에 닿음. 워커가 죽어 RUNNING 인 채로 200 이 계속 오면 3 으로도 안 멈춘다.
 *    1.0 은 사람이 모달을 닫아 끝났는데 이 화면은 모달이 없어 스스로 끊어야 한다.
 */
export const outboundProgressRefetchInterval = (
  progress: OutboundProgress | null | undefined,
  /** **연속** 실패 수다. 누적을 넣으면 상한에 훨씬 빨리 닿아 멀쩡한 폴링이 끊긴다. */
  consecutiveFailureCount: number,
  intervalMs: number = OUTBOUND_PROGRESS_POLLING_INTERVAL_MS,
  attemptCount = 0,
): number | false => {
  if (consecutiveFailureCount >= OUTBOUND_PROGRESS_MAX_FAILURES) return false
  if (attemptCount >= OUTBOUND_PROGRESS_MAX_ATTEMPTS) return false

  const status = progress?.status
  if (status && (OUTBOUND_PROGRESS_TERMINAL_STATUSES as readonly string[]).includes(status)) return false
  if (progress && progress.total > 0 && progress.processed >= progress.total) return false

  return intervalMs
}

/**
 * 저장 진행률 폴링. `progressKey` 가 없으면 조회하지 않는다.
 *
 * 종료 조건은 [outboundProgressRefetchInterval] 참고. 호출부도 progressKey 를 비우지만,
 * 그쪽이 실패해도 요청이 무한히 나가지 않게 훅에서 한 번 더 막는다.
 */
/**
 * 진행률 쿼리 + **폴링이 끝까지 못 가고 멈췄는지** 판정.
 *
 * 완료·실패로 정상 종료한 경우는 `isExhausted` 가 거짓이다. 그쪽은 호출부가 `data.status` 로 가른다.
 *
 * 판정을 훅이 내보내는 이유는 세는 곳을 하나로 두기 위해서다. 호출부가 따로 세면 정의가 어긋난다 —
 * 실제로 화면이 성공만 세고 훅이 성공+실패를 세는 바람에, 실패가 상한 미만으로 섞이면 훅은 폴링을
 * 끊는데 화면은 상한에 못 미쳐 저장 잠금이 풀리지 않는 구멍이 있었다.
 */
export type OutboundProgressResult = UseQueryResult<OutboundProgress | null, Error> & {
  isExhausted: boolean
}

export const useGetOutboundProgress = (
  progressKey: string | null,
  intervalMs: number = OUTBOUND_PROGRESS_POLLING_INTERVAL_MS,
): OutboundProgressResult => {
  /**
   * **연속** 실패 수. react-query 가 주는 값으로는 셀 수 없다 — `fetchFailureCount` 는 폴링 회차마다
   * 0 으로 돌아가고, `errorUpdateCount` 는 성공해도 안 줄어 누적이지 연속이 아니다. 1.0
   * `progressPollingEpic` 은 성공하면 0 으로 되돌렸고, 그래야 잠깐 끊긴 네트워크로 폴링이 죽지 않는다.
   *
   * 시도 횟수까지 같이 들고 있다. `queryFn` 안에서 센다 — 렌더에서 세면 늦다. react-query 는
   * `queryFn` 이 끝난 직후 다음 `refetchInterval` 을 계산하고 렌더는 그 뒤라, 한 박자씩 밀린다.
   */
  const counts = useRef({ consecutiveFailures: 0, attempts: 0 })

  // 폴링 키가 바뀌면 새 저장이다 — 처음부터 센다.
  useEffect(() => {
    counts.current = { consecutiveFailures: 0, attempts: 0 }
  }, [progressKey])

  const query = useQuery({
    queryKey: generalOutboundQueryKeys.progress(progressKey ?? ''),
    queryFn: async () => {
      counts.current.attempts += 1
      try {
        const response = await getOutboundProgress(progressKey as string)
        counts.current.consecutiveFailures = 0
        return response.data ?? null
      } catch (error) {
        counts.current.consecutiveFailures += 1
        throw error
      }
    },
    enabled: Boolean(progressKey),
    // 목록 쿼리와 같은 이유다. 전역 `QueryCache.onError` 가 실패마다 알럿을 띄우는데, 폴링은 10초마다
    // 돌아 그 알럿이 반복해서 쌓인다. 화면이 상한에서 자기 문구로 한 번 안내한다.
    meta: { noErrorMessage: true },
    /**
     * 지우면 상한에 닿아도 저장 잠금이 안 풀린다.
     *
     * v5 기본값은 tracked 다. `trackResult` 가 결과의 각 키를 getter 로 감싸 읽는 순간만 추적하고
     * (`queryObserver.js:137-151`), 통지는 그렇게 추적된 키의 참조가 바뀔 때만 나간다(같은 파일 400-412).
     * 아래 `isExhausted` 는 `Object.assign` 으로 얹은 평범한 값이라 getter 가 아니고, 호출부는 `data`
     * 하나만 읽는다. 그래서 tracked 가 `data` 뿐이다.
     *
     * 하필 상한이 실제로 걸리는 두 경우가 `data` 참조가 안 변하는 경우다. 연속 실패로 끝나면 `data` 는
     * 계속 `undefined` 고, 워커가 RUNNING 에 멈춰 같은 payload 를 주면 `structuralSharing` 이 이전
     * 참조를 그대로 돌려준다. 둘 다 통지가 없어 화면이 판정을 못 받고, 저장·확정·업로드가 안내 없이
     * 잠긴 채 남는다.
     *
     * 폴링 전용 쿼리라 매 회차 통지해도 부담이 없다.
     */
    notifyOnChangeProps: 'all',
    refetchInterval: (q) =>
      outboundProgressRefetchInterval(
        q.state.data,
        counts.current.consecutiveFailures,
        intervalMs,
        counts.current.attempts,
      ),
  })

  // 폴링 주기 판정과 **같은 카운터**를 읽는다. ref 라 이 값이 바뀌어도 렌더가 안 나므로, 판정이
  // 화면까지 가는 건 전적으로 위 `notifyOnChangeProps: 'all'` 이 만드는 회차별 통지에 얹혀 있다.
  const isExhausted =
    Boolean(progressKey) &&
    (counts.current.consecutiveFailures >= OUTBOUND_PROGRESS_MAX_FAILURES ||
      counts.current.attempts >= OUTBOUND_PROGRESS_MAX_ATTEMPTS)

  return Object.assign(query, { isExhausted })
}
